import { z } from 'zod';

import type { GameControllerGetGameResponse, GetGamesResultDto } from '~/api';
import { zCreateGameDto } from '~/api/zod.gen';

export type Game = GameControllerGetGameResponse['data'];
export type GamesListItem = GetGamesResultDto['data'][number];
export type GameStatusType = Game['status'];
export type CardType = NonNullable<Game['fouls']>[number]['card'];
export type CreateGameParams = Omit<Game, 'id'>;

const foulsArray = zCreateGameDto.shape.fouls.unwrap();
const participantsArray = zCreateGameDto.shape.participants.unwrap();
const participantElement = participantsArray.element;
const foulElement = foulsArray.element;

export const GameStatusEnum = zCreateGameDto.shape.status;
export const CardTypeEnum = foulElement.shape.card;

const goalItemSchema = zCreateGameDto.shape.goals._def.innerType.element.omit({ teamMembershipId: true }).extend({
  teamMembership: z.object(
    {
      value: z.number(),
      label: z.string(),
    },
    { required_error: 'required', invalid_type_error: 'required' }
  ),
});

const passItemSchema = zCreateGameDto.shape.passes._def.innerType.element.omit({ teamMembershipId: true }).extend({
  teamMembership: z.object(
    {
      value: z.number(),
      label: z.string(),
    },
    { required_error: 'required', invalid_type_error: 'required' }
  ),
});

const refereeItemSchema = zCreateGameDto.shape.referees._def.innerType.element.omit({ refereeId: true }).extend({
  referee: z.object(
    {
      value: z.number(),
      label: z.string(),
    },
    { required_error: 'required', invalid_type_error: 'required' }
  ),
});

const foulItemSchema = foulElement.omit({ teamMembershipId: true, card: true }).extend({
  teamMembership: z.object(
    {
      value: z.number(),
      label: z.string(),
    },
    { required_error: 'required', invalid_type_error: 'required' }
  ),
  card: z.object(
    {
      value: CardTypeEnum,
      label: z.string(),
    },
    { required_error: 'required', invalid_type_error: 'required' }
  ),
});

export const createGameFormSchema = zCreateGameDto
  .omit({
    teamOneId: true,
    participants: true,
    teamTwoId: true,
    status: true,
    tourId: true,
    goals: true,
    passes: true,
    referees: true,
  })
  .extend({
    teamOneId: z.object({ value: z.number(), label: z.string() }).optional(),
    teamTwoId: z.object({ value: z.number(), label: z.string() }).optional(),
    tour: z.object(
      { value: z.number(), label: z.string() },
      { required_error: 'required', invalid_type_error: 'required' }
    ),
    status: z.object({ value: GameStatusEnum, label: z.string() }),
    participants: z.array(participantElement.omit({ id: true }).extend({ id: z.number().optional() })).optional(),
    goals: z.array(goalItemSchema).optional(),
    fouls: z.array(foulItemSchema).optional(),
    passes: z.array(passItemSchema).optional(),
    referees: z.array(refereeItemSchema).optional(),
  });

type ParticipantItem = Omit<NonNullable<CreateGameParams['participants']>[number], 'id'> & {
  id?: number;
};
type GoalItem = Omit<NonNullable<CreateGameParams['goals']>[number], 'teamMembershipId' | 'teamMembership'> & {
  teamMembership: { value: number; label: string };
};
type PassItem = Omit<NonNullable<CreateGameParams['passes']>[number], 'teamMembershipId' | 'teamMembership'> & {
  teamMembership: { value: number; label: string };
};
type RefereeItem = Omit<NonNullable<CreateGameParams['referees']>[number], 'refereeId' | 'referee'> & {
  referee: { value: number; label: string };
};

type FoulItem = Omit<NonNullable<CreateGameParams['fouls']>[number], 'teamMembershipId' | 'teamMembership' | 'card'> & {
  teamMembership: { value: number; label: string };
  card: { value: CardType; label: string };
};
export interface CreateGameFormValues extends Omit<
  CreateGameParams,
  | 'teamOneId'
  | 'teamTwoId'
  | 'teamOne'
  | 'teamTwo'
  | 'status'
  | 'tourId'
  | 'tour'
  | 'tournament'
  | 'goals'
  | 'fouls'
  | 'passes'
  | 'participants'
  | 'referees'
> {
  teamOne: { value: number; label: string } | null;
  teamTwo: { value: number; label: string } | null;
  tour: { value: number; label: string } | null;
  status: { value: CreateGameParams['status']; label: string };
  participants?: ParticipantItem[];
  goals?: GoalItem[];
  fouls?: FoulItem[];
  passes?: PassItem[];
  referees?: RefereeItem[];
}
